import { useEffect, useState, useRef, useCallback } from "react";
import { MapPin, Navigation, Search, Loader2, Check, Home, Briefcase, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const TEAL = "#005C66";
const LABELS = [
  { key: "Home", Icon: Home },
  { key: "Work", Icon: Briefcase },
  { key: "Other", Icon: MapPin },
];

// Google-Places-powered address picker with current-location, exact-address line,
// label + save-for-future. Calls onSelect({ formattedAddress, exactAddress, lat, lng, addressId }).
export const AddressPicker = ({ userId, onSelect }) => {
  const [saved, setSaved] = useState([]);
  const [query, setQuery] = useState("");
  const [preds, setPreds] = useState([]);
  const [locating, setLocating] = useState(false);
  const [draft, setDraft] = useState(null); // {formattedAddress, lat, lng, placeId}
  const [exact, setExact] = useState("");
  const [label, setLabel] = useState("Home");
  const [save, setSave] = useState(true);
  const [error, setError] = useState("");
  const timer = useRef(null);

  const loadSaved = useCallback(async () => {
    try {
      const res = await api.get(`/patient/${userId}/addresses`);
      setSaved(res.data?.addresses || []);
    } catch { /* ignore */ }
  }, [userId]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const onQuery = (val) => {
    setQuery(val);
    if (timer.current) clearTimeout(timer.current);
    if (!val || val.trim().length < 3) { setPreds([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await api.post(`/places/autocomplete`, { input_text: val.trim(), country_code: "IN" });
        setPreds(res.data?.predictions || []);
      } catch { setPreds([]); }
    }, 300);
  };

  const pickPrediction = async (p) => {
    setPreds([]);
    setQuery(p.description);
    try {
      const res = await api.post(`/places/details`, { place_id: p.place_id });
      const place = res.data?.place;
      if (place) {
        setDraft({ formattedAddress: place.formatted_address || p.description, lat: place.latitude, lng: place.longitude, placeId: p.place_id });
      } else {
        setDraft({ formattedAddress: p.description, lat: null, lng: null, placeId: p.place_id });
      }
    } catch {
      setDraft({ formattedAddress: p.description, lat: null, lng: null, placeId: p.place_id });
    }
  };

  const useCurrentLocation = () => {
    setError("");
    if (!navigator.geolocation) { setError("Location is not available in this browser."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const res = await api.get(`/location/reverse-geocode`, { params: { lat: latitude, lng: longitude } });
        setDraft({ formattedAddress: res.data?.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`, lat: latitude, lng: longitude, placeId: null });
      } catch {
        setDraft({ formattedAddress: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`, lat: latitude, lng: longitude, placeId: null });
      } finally { setLocating(false); }
    }, () => { setError("Please allow location access."); setLocating(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const confirm = async () => {
    if (!draft) { setError("Search or pick a location first."); return; }
    if (!draft.lat || !draft.lng) { setError("We couldn't get coordinates for this place. Try another or use current location."); return; }
    let addressId = null;
    if (save) {
      try {
        const res = await api.post(`/patient/${userId}/addresses`, {
          label, formatted_address: draft.formattedAddress, exact_address: exact.trim(),
          place_id: draft.placeId, lat: draft.lat, lng: draft.lng, is_default: saved.length === 0,
        });
        addressId = res.data?.address?.id;
      } catch { /* non-blocking */ }
    }
    onSelect?.({ formattedAddress: draft.formattedAddress, exactAddress: exact.trim(), lat: draft.lat, lng: draft.lng, addressId, label });
  };

  const pickSaved = (a) => {
    onSelect?.({ formattedAddress: a.formattedAddress, exactAddress: a.exactAddress || "", lat: a.lat, lng: a.lng, addressId: a.id, label: a.label });
  };

  const removeSaved = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/patient/${userId}/addresses/${id}`); loadSaved(); } catch { /* ignore */ }
  };

  return (
    <div data-testid="address-picker">
      {saved.length > 0 && !draft && (
        <div className="mb-4">
          <p className="text-sm font-semibold mb-2">Saved addresses</p>
          <div className="space-y-2">
            {saved.map((a) => (
              <button key={a.id} data-testid={`saved-address-${a.id}`} onClick={() => pickSaved(a)} className="flex items-start gap-3 w-full text-left p-3 rounded-xl border hover:bg-accent" style={{ borderColor: "#E2E8F0" }}>
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{a.label}</span>
                    {a.isDefault && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Default</span>}
                  </span>
                  <span className="block text-xs text-muted-foreground truncate">{a.exactAddress ? `${a.exactAddress}, ` : ""}{a.formattedAddress}</span>
                </span>
                <span onClick={(e) => removeSaved(e, a.id)} className="text-muted-foreground hover:text-red-500" data-testid={`saved-address-del-${a.id}`}><Trash2 className="h-4 w-4" /></span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 my-3 text-xs text-muted-foreground"><span className="flex-1 h-px bg-border" /> or add new <span className="flex-1 h-px bg-border" /></div>
        </div>
      )}

      <Button variant="outline" onClick={useCurrentLocation} disabled={locating} className="w-full rounded-xl mb-3" data-testid="address-current-location">
        {locating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Navigation className="h-4 w-4 mr-2" style={{ color: TEAL }} />}
        Use current location
      </Button>

      <div className="relative mb-2">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="address-search" className="pl-9" placeholder="Search for area, street, landmark…" value={query} onChange={(e) => onQuery(e.target.value)} />
        {preds.length > 0 && (
          <div className="absolute z-30 left-0 right-0 mt-1 bg-background border rounded-xl shadow-lg max-h-64 overflow-auto" data-testid="address-predictions">
            {preds.map((p) => (
              <button key={p.place_id} data-testid={`address-pred-${p.place_id}`} onMouseDown={(e) => { e.preventDefault(); pickPrediction(p); }} className="flex items-start gap-2 w-full px-3 py-2 text-left hover:bg-accent">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{p.main_text || p.description}</span>
                  {p.secondary_text && <span className="block text-xs text-muted-foreground truncate">{p.secondary_text}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {draft && (
        <Card className="rounded-2xl p-4 mt-3" data-testid="address-draft">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5" style={{ color: TEAL }} />
            <p className="text-sm font-medium flex-1">{draft.formattedAddress}</p>
          </div>
          <Textarea data-testid="address-exact" className="mt-3" placeholder="House / flat no., tower, landmark" value={exact} onChange={(e) => setExact(e.target.value)} />
          <div className="flex gap-2 mt-3">
            {LABELS.map(({ key, Icon }) => (
              <button key={key} data-testid={`address-label-${key}`} onClick={() => setLabel(key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm ${label === key ? "text-white" : ""}`} style={label === key ? { backgroundColor: TEAL, borderColor: TEAL } : { borderColor: "#D1E7E9" }}>
                <Icon className="h-3.5 w-3.5" /> {key}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="address-save-toggle">
            <span className="h-4 w-4 rounded border-2 flex items-center justify-center" style={{ borderColor: TEAL, backgroundColor: save ? TEAL : "transparent" }} onClick={() => setSave((s) => !s)}>{save && <Check className="h-3 w-3 text-white" />}</span>
            Save this address for future orders
          </label>
          {error && <p className="text-red-600 text-sm mt-2" data-testid="address-error">{error}</p>}
          <Button onClick={confirm} className="w-full rounded-xl mt-3" style={{ backgroundColor: TEAL }} data-testid="address-confirm">
            <Plus className="h-4 w-4 mr-2" /> Confirm address
          </Button>
        </Card>
      )}
      {!draft && error && <p className="text-red-600 text-sm mt-2" data-testid="address-error">{error}</p>}
    </div>
  );
};
